import {announce} from '@github-ui/aria-live'
import {useAnalytics} from '@github-ui/use-analytics'
import useIsMounted from '@github-ui/use-is-mounted'
import {useActiveRoute} from '@github-ui/pull-requests/hooks/use-active-route'
import {ScopedCommands} from '@github-ui/ui-commands'
import {FocusKeys} from '@primer/behaviors'
import {FormControl, Spinner, Textarea, TextInput, useFocusZone, IconButton, CounterLabel} from '@primer/react'
import type {DialogProps} from '@primer/react/experimental'
import {Dialog} from '@primer/react/experimental'
import {type PropsWithChildren, useCallback, useEffect, useReducer, useRef, useState} from 'react'
import {FileIcon, XIcon} from '@primer/octicons-react'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {DiscardBatchConfirmationDialog} from '../DiscardBatchConfirmationDialog'
import type {SuggestionIdentifier, SuggestionDetails} from '../../../conversation-types'
import {AutomatedReviewDiffEntry} from '../AutomatedReview/AutomatedReviewDiffEntry'
import {
  isSuggestionGenerationApplicable,
  useGenerateSuggestionCommitMessage,
  type SuggestionCommitMessageGenerationTerminalState,
  type SuggestionGenerationConfig,
} from '../../../hooks/use-generate-suggestion-commit-message'

function DialogFooter({children, footerButtons}: PropsWithChildren<Pick<DialogProps, 'footerButtons'>>) {
  const {containerRef: footerRef} = useFocusZone({
    bindKeys: FocusKeys.ArrowHorizontal | FocusKeys.Tab,
    focusInStrategy: 'closest',
  })
  return footerButtons ? (
    <Dialog.Footer ref={footerRef as React.RefObject<HTMLDivElement | null>}>
      <div className="d-flex flex-column">
        <div className="d-flex flex-row gap-2 flex-justify-end">
          <Dialog.Buttons buttons={footerButtons} />
        </div>
        {children}
      </div>
    </Dialog.Footer>
  ) : null
}

type FormState = {
  commitMessage: string
  extendedDescription: string
}

type FormAction = {
  type: ThisType<typeof actions.CHANGE_COMMIT_MESSAGE> | ThisType<typeof actions.CHANGE_EXTENDED_DESCRIPTION>
  payload: string
}

const actions = {
  CHANGE_COMMIT_MESSAGE: 'CHANGE_COMMIT_MESSAGE',
  CHANGE_EXTENDED_DESCRIPTION: 'CHANGE_EXTENDED_DESCRIPTION',
}

const baseErrorMessage = 'There was an error trying to commit changes'

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case actions.CHANGE_COMMIT_MESSAGE: {
      return {
        ...state,
        commitMessage: action.payload,
      }
    }
    case actions.CHANGE_EXTENDED_DESCRIPTION:
      return {...state, extendedDescription: action.payload}
    default:
      // eslint-disable-next-line no-console
      console.error('Unknown suggested change action.')
      return state
  }
}

function getCommitMessage(authorLogins: string[]) {
  // if there's only one suggestion
  if (authorLogins.length === 1) {
    return `Apply suggestion from @${authorLogins[0]}`
  }

  // if multiple suggestions that all have the same login
  if (new Set(authorLogins).size === 1) {
    return `Apply suggestions from @${authorLogins[0]}`
  }

  // multiple suggestions with different logins
  return `Apply suggestions from code review`
}

export type ApplySuggestionDialogProps = {
  authorLogins: string[]
  batchSize: number
  commentKind: 'ccr' | 'human' | 'mixed'
  onClose: () => void
  onCommit: (
    commitMessage: string,
    onError: (error: Error, type?: string, friendlyMessage?: string) => void,
    onCompleted: () => void,
  ) => void
  defaultCommitMessage?: string
  /**
   * Prebuilt diff-hunk references + a stable identifier for generating a commit message title via
   * the commit-message-generation agent. Only consumed when `commentKind === 'ccr'`; ignored for
   * all other callers (human, mixed, batch-toolbar, code-scanning, code-quality, etc.).
   */
  generationConfig?: SuggestionGenerationConfig
  dialogTitle?: string
  headerContent?: React.ReactNode
  submitDisabled?: boolean
  suggestions?: SuggestionDetails[]
  onRemoveSuggestion?: (suggestionId: SuggestionIdentifier) => void
  onDiscardBatch?: () => void
  returnFocusRef?: React.RefObject<HTMLElement | null>
  submitButtonLabel?: string
}

export function ApplySuggestionDialog({
  authorLogins,
  batchSize,
  commentKind,
  onClose,
  onCommit,
  defaultCommitMessage,
  generationConfig,
  dialogTitle,
  headerContent,
  submitDisabled,
  suggestions,
  onRemoveSuggestion,
  onDiscardBatch,
  returnFocusRef,
  submitButtonLabel,
}: ApplySuggestionDialogProps) {
  const isMounted = useIsMounted()
  const {sendAnalyticsEvent} = useAnalytics()
  const surface = useActiveRoute()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const fallbackCommitMessage = defaultCommitMessage || getCommitMessage(authorLogins)
  // Computed synchronously from props so the very first render already knows whether to seed the
  // field empty for the "Copilot is thinking..." placeholder treatment below.
  const generationApplicable = isSuggestionGenerationApplicable({commentKind, generationConfig})
  const [formValues, setFormValues] = useReducer(formReducer, {
    commitMessage: generationApplicable ? '' : fallbackCommitMessage,
    extendedDescription: '',
  })
  // Tracks whether the user has directly edited the commit message field, so a later-arriving
  // generated title never clobbers something the user already typed.
  const hasEditedCommitMessageRef = useRef(false)
  const hasSubmissionStartedRef = useRef(false)
  const commitMessageInputRef = useRef<HTMLInputElement>(null)
  const handleGenerationSettled = useCallback(
    (settledState: SuggestionCommitMessageGenerationTerminalState) => {
      const eventNameByStatus: Record<SuggestionCommitMessageGenerationTerminalState['status'], string> = {
        success: 'comments.commit_message_generation_succeeded',
        'no-title': 'comments.commit_message_generation_fallback',
        error: 'comments.commit_message_generation_errored',
      }
      sendAnalyticsEvent(eventNameByStatus[settledState.status], 'CCR_COMMIT_MESSAGE_GENERATION', {
        automatedCommentId: generationConfig?.automatedCommentId,
        durationMs: settledState.durationMs,
      })
    },
    [generationConfig?.automatedCommentId, sendAnalyticsEvent],
  )
  const {generatedCommitMessage, generationState} = useGenerateSuggestionCommitMessage({
    commentKind,
    generationConfig,
    onGenerationSettled: handleGenerationSettled,
  })
  // Snapshot of the generated title at the moment it was applied, used both to show the
  // attribution caption and to drive the one-time aria-live announcement below -- kept separate
  // from `formValues.commitMessage` so later user edits to the field don't re-trigger the
  // announcement or falsely re-attribute edited text to Copilot.
  const [announcedCommitMessage, setAnnouncedCommitMessage] = useState<string | undefined>(undefined)
  const isCommitMessageBlank = !formValues.commitMessage.trim()
  const isCommitMessageGenerationPending =
    generationApplicable &&
    isCommitMessageBlank &&
    (generationState.status === 'idle' || generationState.status === 'loading')
  const isUsingFallbackCommitMessage =
    generationApplicable &&
    formValues.commitMessage === fallbackCommitMessage &&
    (generationState.status === 'error' || generationState.status === 'no-title')
  const isSubmitBlocked = Boolean(submitDisabled) || isCommitMessageBlank
  const resolvedSubmitButtonLabel = onDiscardBatch ? 'Apply suggestions' : (submitButtonLabel ?? 'Apply Suggestion')
  useEffect(() => {
    if (hasEditedCommitMessageRef.current || isSubmitting || hasSubmissionStartedRef.current) return

    if (generationState.status === 'success' && generatedCommitMessage?.commitMessage) {
      if (formValues.commitMessage === generatedCommitMessage.commitMessage) return
      setFormValues({type: actions.CHANGE_COMMIT_MESSAGE, payload: generatedCommitMessage.commitMessage})
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing local UI state (attribution caption + announcement snapshot) to the react-query-backed generationState, same pattern already used in AutomatedReviewSuggestionStates.tsx
      setAnnouncedCommitMessage(generatedCommitMessage.commitMessage)
      announce('Commit message updated with a generated suggestion.', {
        element: commitMessageInputRef.current ?? undefined,
      })
      return
    }

    // Generation ran without producing a usable title or became unauthorized. Restore the generic
    // fallback while the field is still blank or contains the unedited generated title, but
    // preserve anything the user typed.
    const shouldUseFallback =
      !generationApplicable || generationState.status === 'error' || generationState.status === 'no-title'
    const hasUneditedGeneratedCommitMessage =
      announcedCommitMessage !== undefined && formValues.commitMessage === announcedCommitMessage
    if (shouldUseFallback && (!formValues.commitMessage || hasUneditedGeneratedCommitMessage)) {
      setFormValues({type: actions.CHANGE_COMMIT_MESSAGE, payload: fallbackCommitMessage})
      setAnnouncedCommitMessage(undefined)
      announce("Copilot couldn't generate a commit message. Using the default commit message instead.", {
        element: commitMessageInputRef.current ?? undefined,
      })
    }
  }, [
    announcedCommitMessage,
    fallbackCommitMessage,
    formValues.commitMessage,
    generatedCommitMessage,
    generationApplicable,
    generationState,
    isSubmitting,
  ])

  const handleError = useCallback(
    (_error: Error, _type?: string, friendlyMessage?: string) => {
      if (!isMounted()) return

      let serverErrorMessage = baseErrorMessage
      if (friendlyMessage) serverErrorMessage += `: ${friendlyMessage}`
      else serverErrorMessage += '.'

      setIsSubmitting(false)
      setErrorMessage(serverErrorMessage)
    },
    [isMounted],
  )

  const handleCompleted = useCallback(() => {
    if (!isMounted()) return
    setIsSubmitting(false)
    if (errorMessage) setErrorMessage(undefined)
  }, [errorMessage, isMounted])

  const handleOnCommitClick = useCallback(() => {
    if (isSubmitting || isSubmitBlocked) return

    hasSubmissionStartedRef.current = true
    setIsSubmitting(true)
    sendAnalyticsEvent('comments.commit_suggestion', 'COMMIT_SUGGESTION_DIALOG_BUTTON', {
      batchSize,
      commentKind,
      surface,
    })
    onCommit(`${formValues.commitMessage}\n\n${formValues.extendedDescription}`, handleError, handleCompleted)
  }, [
    batchSize,
    commentKind,
    formValues.commitMessage,
    formValues.extendedDescription,
    handleCompleted,
    handleError,
    isSubmitBlocked,
    isSubmitting,
    onCommit,
    sendAnalyticsEvent,
    surface,
  ])

  const handleDiscardClick = useCallback(() => {
    setShowDiscardConfirmation(true)
  }, [])

  const handleDiscardConfirm = useCallback(() => {
    setShowDiscardConfirmation(false)
    onDiscardBatch?.()
  }, [onDiscardBatch])

  const handleDiscardCancel = useCallback(() => {
    setShowDiscardConfirmation(false)
  }, [])

  const title = batchSize > 1 ? 'Apply suggestions' : 'Apply suggestion'
  const showCounterLabel = (suggestions ?? []).length > 0 || batchSize > 0

  const renderFooter = () => {
    if (onDiscardBatch) {
      return (
        <Dialog.Footer>
          <div className="d-flex flex-column width-full">
            <div className="d-flex flex-row flex-justify-between width-full">
              <Dialog.Buttons
                buttons={[{content: 'Discard batch', onClick: handleDiscardClick, buttonType: 'danger'}]}
              />
              <div className="d-flex flex-row gap-2">
                <Dialog.Buttons
                  buttons={[
                    {content: 'Cancel', onClick: onClose},
                    {
                      content: (
                        <span className="d-flex flex-row flex-items-center">
                          Apply suggestions
                          {showCounterLabel && (
                            <CounterLabel className="ml-1 color-fg-inherit">
                              {suggestions?.length || batchSize}
                            </CounterLabel>
                          )}
                          {isSubmitting && <Spinner size="small" className="ml-1" />}
                        </span>
                      ),
                      onClick: handleOnCommitClick,
                      buttonType: 'primary',
                      disabled: isSubmitBlocked,
                    },
                  ]}
                />
              </div>
            </div>
            {errorMessage && (
              <FormControl.Validation variant="error" className="mt-2 pt-1 flex-justify-end">
                {errorMessage}
              </FormControl.Validation>
            )}
          </div>
        </Dialog.Footer>
      )
    }

    return (
      <DialogFooter
        footerButtons={[
          {content: 'Cancel', onClick: onClose},
          {
            content: (
              <span className="d-flex flex-row flex-items-center">
                <span>{resolvedSubmitButtonLabel}</span>
                {isSubmitting && <Spinner size="small" className="ml-1" />}
              </span>
            ),
            onClick: handleOnCommitClick,
            buttonType: 'primary',
            disabled: isSubmitBlocked,
          },
        ]}
      >
        {errorMessage && (
          <FormControl.Validation variant="error" className="mt-2 pt-1 flex-justify-end">
            {errorMessage}
          </FormControl.Validation>
        )}
      </DialogFooter>
    )
  }

  return (
    <>
      <ScopedCommands commands={{'github:submit-form': handleOnCommitClick}}>
        <Dialog
          title={dialogTitle || title}
          renderFooter={renderFooter}
          onClose={onClose}
          returnFocusRef={returnFocusRef}
        >
          {headerContent}
          <FormControl className="mb-2" required>
            <FormControl.Label required>Commit message</FormControl.Label>
            <TextInput
              block
              name="commit_message"
              ref={commitMessageInputRef}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                hasEditedCommitMessageRef.current = true
                setAnnouncedCommitMessage(undefined)
                setFormValues({type: actions.CHANGE_COMMIT_MESSAGE, payload: event.target.value})
              }}
              placeholder={isCommitMessageGenerationPending ? 'Copilot is thinking...' : undefined}
              value={formValues.commitMessage}
            />
            {isCommitMessageGenerationPending ? (
              <FormControl.Caption>
                Copilot is generating a commit message. Enter your own message or wait for Copilot to finish.
              </FormControl.Caption>
            ) : isUsingFallbackCommitMessage ? (
              <FormControl.Caption>
                Copilot couldn&apos;t generate a commit message. Using the default commit message instead.
              </FormControl.Caption>
            ) : announcedCommitMessage !== undefined ? (
              <FormControl.Caption>Message suggested by Copilot.</FormControl.Caption>
            ) : null}
          </FormControl>
          <FormControl>
            <FormControl.Label>Extended description</FormControl.Label>
            <Textarea
              block
              rows={3}
              placeholder="Add an optional description..."
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFormValues({type: actions.CHANGE_EXTENDED_DESCRIPTION, payload: event.target.value})
              }
              value={formValues.extendedDescription}
            />
          </FormControl>
          {suggestions && suggestions.length > 0 && (
            <div className="tmp-mb-3 tmp-mt-3">
              <p className="f6 text-bold mb-2">
                Suggestions to apply <CounterLabel>{suggestions.length}</CounterLabel>
              </p>
              <div
                style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                }}
              >
                {suggestions.map(suggestion => (
                  <div key={suggestion.commentDatabaseId} className="border rounded-2 mb-2">
                    <div
                      className="d-flex flex-items-center flex-justify-between p-2"
                      style={{
                        transition: 'background-color 0.1s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--bgColor-muted, #f6f8fa)')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <FileIcon className="mr-2" size={16} />
                      <span
                        className="flex-1 text-mono f6"
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {suggestion.path}
                      </span>
                      {onRemoveSuggestion && (
                        <IconButton
                          aria-label={`Remove suggestion from ${suggestion.path}`}
                          icon={XIcon}
                          size="small"
                          variant="invisible"
                          onClick={() =>
                            onRemoveSuggestion({
                              commentDatabaseId: suggestion.commentDatabaseId,
                              suggestionIndex: suggestion.suggestionIndex,
                            })
                          }
                        />
                      )}
                    </div>
                    <div className="p-2">
                      <VerifiedHTMLDiv
                        className="markdown-body"
                        comment-testid={`Comment body html for comment`}
                        /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
                        html={suggestion.bodyHTML as SafeHTMLString}
                      />
                      {suggestion.diffEntries && suggestion.diffEntries.length > 0 && (
                        <div className="mt-2">
                          {suggestion.diffEntries.map(diffEntry => (
                            <AutomatedReviewDiffEntry key={diffEntry.path} diffEntry={diffEntry} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Dialog>
      </ScopedCommands>
      <DiscardBatchConfirmationDialog
        isOpen={showDiscardConfirmation}
        onConfirm={handleDiscardConfirm}
        onCancel={handleDiscardCancel}
        suggestionsCount={suggestions?.length || batchSize}
      />
    </>
  )
}
